import { Dictionary, keyBy } from 'lodash';

import {
	IAzureClassificationNode,
	IAzureField,
	IAzureFields,
	IAzureFieldType,
	IAzureProject,
	IAzureUser,
	OptionType
} from '@evinced-private/ui-common';

import {
	addAttachmentToPayload,
	dataURItoBlob,
	getClassificationNodes,
	getFieldsMetaData,
	getFormattedFields,
	getOrganizationNameUrl,
	getPaths,
	getProjectNameUrl,
	getProjectsOptionType,
	getProjectTypesOptions,
	getUsersUrl,
	openUrlInNewTab
} from '../components/azure-issue-creator/Helpers/AzureHelper';
import { AZURE_URL_PATHS } from '../components/azure-issue-creator/Helpers/UrlPaths';
import { logger } from '../services/Logger';
import { IAzureOrganization, IAzureProjectType } from '../types/AzureTypes';
import { ITokenData } from '../types/JiraTypes';

import {
	getAzureAccessToken,
	getUserFromLocalStorage,
	setAzureAccessToken
} from './LocalStorageApi';

const {
	API_VERSION,
	WORK_ITEM_FIELDS,
	WORK_ITEM_TYPE,
	PROJECTS,
	WORK_ITEMS,
	PROJECT_TYPES,
	USERS,
	FIELDS,
	CLASSIFICATIONS,
	USER,
	ATTACHMENT
} = AZURE_URL_PATHS;

const BYPASS_RULES_QUERY_PARAM = 'bypassRules';
const BYPASS_RULES_QUERY_VALUE = 'true';

/**
 * Append a path (and optional `?query`) to an absolute Azure REST base URL using the {@link URL} API.
 */
const appendAzurePath = (baseHref: string, pathAndOptionalQuery: string): URL => {
	const url = new URL(baseHref);
	const qIndex = pathAndOptionalQuery.indexOf('?');
	const pathnamePart = qIndex >= 0 ? pathAndOptionalQuery.slice(0, qIndex) : pathAndOptionalQuery;
	const searchPart = qIndex >= 0 ? pathAndOptionalQuery.slice(qIndex + 1) : '';
	const root = url.pathname.replace(/\/$/, '');
	const suffix = pathnamePart.startsWith('/') ? pathnamePart : `/${pathnamePart}`;
	url.pathname = `${root}${suffix}`;
	if (searchPart) {
		new URLSearchParams(searchPart).forEach((value, key) => {
			url.searchParams.set(key, value);
		});
	}
	return url;
};

const mergeApiVersionParams = (url: URL): void => {
	new URLSearchParams(API_VERSION).forEach((value, key) => {
		url.searchParams.set(key, value);
	});
};

const azureAuthURL = import.meta.env.VITE_AZURE_AUTH_TOKEN_URL as string;
const clientID = import.meta.env.VITE_AZURE_APP_CLIENT_ID as string;
const clientSecret = import.meta.env.VITE_AZURE_APP_CLIENT_SECRET as string;
const redirectURL = import.meta.env.VITE_AZURE_REDIRECT_URL as string;
const scopes = 'vso.graph vso.identity vso.work_full';

const getAzureAuthURL = (): string => {
	const userID = getUserFromLocalStorage();
	// used for 'state' param (required for security) - a value that is associated with the user
	// that is directed to the authorization URL
	const state = userID?.userId;
	const url = new URL('https://app.vssps.visualstudio.com/oauth2/authorize');
	url.searchParams.set('client_id', clientID);
	url.searchParams.set('response_type', 'Assertion');
	url.searchParams.set('state', String(state ?? ''));
	url.searchParams.set('scope', scopes);
	url.searchParams.set('redirect_uri', redirectURL);
	return url.href;
};
const innerCallApi = async (
	url: string,
	method = 'GET',
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	payload?: any,
	withToken = true,
	contentType = 'application/json-patch+json'
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
	const requestHeaders: HeadersInit = new Headers();

	requestHeaders.set('Content-Type', contentType);
	requestHeaders.set('Accept', 'application/json-patch+json');

	if (withToken) {
		const azureToken: ITokenData = JSON.parse(getAzureAccessToken());
		if (azureToken?.access_token) {
			requestHeaders.set('Authorization', `Bearer ${azureToken.access_token}`);
		} else {
			return Promise.reject(new Error('Unauthorized'));
		}
	}

	const fetchOptions: RequestInit = {
		mode: 'cors',
		method,
		headers: requestHeaders,
		body: null
	};

	if (payload) {
		fetchOptions.body =
			contentType === 'application/json-patch+json' ? JSON.stringify(payload) : payload;
	}
	logger.debug('request started: ', url);
	try {
		let response = await fetch(url, fetchOptions);

		if (response.status === 203) {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			await refreshAzureToken();
			response = await innerCallApi(url, method, payload, withToken, contentType);
		}
		if (!response.ok) {
			logger.error('cannot call API', url, fetchOptions, response);
			throw response.status;
		}
		logger.debug('request complete successfully: ', url);
		const json = await response.json();
		logger.debug('Response for ', url, 'is:', json);
		return json;
	} catch (err) {
		logger.error('error for request: ', url, ' with options: ', fetchOptions, 'Error', err);
		throw err;
	}
};

const getAndSaveAzureToken = async (code: string): Promise<void> => {
	const azureToken = getAzureAccessToken();
	if (azureToken) {
		window.close();
		return;
	}

	const payload = `client_assertion_type=urn:ietf:params:oauth:client-assertion-type:jwt-bearer&client_assertion=${clientSecret}&grant_type=urn:ietf:params:oauth:grant-type:jwt-bearer&assertion=${code}&redirect_uri=${redirectURL}`;
	const data = await innerCallApi(
		azureAuthURL,
		'POST',
		payload,
		false,
		'application/x-www-form-urlencoded'
	);

	if (data.access_token) {
		setAzureAccessToken(JSON.stringify(data));
		window.close();
	}
};

const refreshAzureToken = async (): Promise<void> => {
	const azureToken = JSON.parse(getAzureAccessToken());

	if (azureToken) {
		const payload = `client_assertion_type=urn:ietf:params:oauth:client-assertion-type:jwt-bearer&client_assertion=${clientSecret}&grant_type=refresh_token&assertion=${azureToken.refresh_token}&redirect_uri=${redirectURL}`;
		const data = await innerCallApi(
			azureAuthURL,
			'POST',
			payload,
			false,
			'application/x-www-form-urlencoded'
		);

		if (data.access_token) {
			setAzureAccessToken(JSON.stringify(data));
		}
	}
};

const getProjects = async (host: string): Promise<IAzureProject[]> => {
	const url = getOrganizationNameUrl(host);
	try {
		const requestUrl = appendAzurePath(url, PROJECTS);
		mergeApiVersionParams(requestUrl);
		const projects = await innerCallApi(requestUrl.href);
		return getProjectsOptionType(projects.value) ?? [];
	} catch (error) {
		logger.error('error for request: getProjects', url, 'Error', error);
		return [];
	}
};

const getUsers = async (host: string): Promise<IAzureUser[]> => {
	const url = getUsersUrl(host);

	try {
		const requestUrl = appendAzurePath(url, USERS);
		const response = (await innerCallApi(requestUrl.href)).value;
		const users = response.filter((user) => {
			if (!user.displayName.includes('Build Service')) {
				return {
					id: user.originId,
					email: user.mailAddress,
					displayName: user.displayName
				};
			}
			return null;
		});

		return users;
	} catch (error) {
		logger.error('error for request: getUsers', url, 'Error', error);
		return [];
	}
};

const getClassification = async (host: string): Promise<IAzureClassificationNode[]> => {
	const requestUrl = appendAzurePath(host, `/${CLASSIFICATIONS}`);
	mergeApiVersionParams(requestUrl);
	try {
		const classification = (await innerCallApi(requestUrl.href)).value;
		return classification;
	} catch (error) {
		logger.error('error for request: getClassification', requestUrl.href, 'Error', error);
		return [];
	}
};

const getSelectedIssueType = async (
	host: string,
	selectedIssueType: string
): Promise<IAzureFieldType[]> => {
	const requestUrl = appendAzurePath(
		host,
		`${WORK_ITEM_TYPE}/${selectedIssueType}${WORK_ITEM_FIELDS}`
	);
	mergeApiVersionParams(requestUrl);
	try {
		const selectedIssueType: IAzureFieldType[] = (await innerCallApi(requestUrl.href)).value;
		return selectedIssueType;
	} catch (error) {
		logger.error('error for request: selectedIssueType', requestUrl.href, 'Error', error);
		return [];
	}
};

const getProjectFields = async (host: string, selectedIssueType: string): Promise<IAzureFields> => {
	const fieldsUrl = appendAzurePath(host, FIELDS.replace(/&$/, ''));
	mergeApiVersionParams(fieldsUrl);
	try {
		let selectedIssueTypeFields: IAzureFieldType[] = await getSelectedIssueType(
			host,
			selectedIssueType
		);
		const fields: Dictionary<IAzureField> = keyBy(
			(await innerCallApi(fieldsUrl.href)).value,
			'referenceName'
		);
		const users: string[] = (await getUsers(host)).map((user: IAzureUser) => user.displayName);
		const classificationNodes: IAzureClassificationNode[] = await getClassification(host);
		const areaNodes: IAzureClassificationNode[] = [classificationNodes[0]];
		const iterationsNodes: IAzureClassificationNode[] = [classificationNodes[1]];
		const areas: string[] = getClassificationNodes(areaNodes);
		const iterations: string[] = getClassificationNodes(iterationsNodes);

		selectedIssueTypeFields = selectedIssueTypeFields.map((field: IAzureFieldType) => {
			if (field.referenceName === 'System.AreaPath') {
				return { ...field, allowedValues: areas, type: 'select' };
			}
			if (field.referenceName === 'System.IterationPath') {
				return { ...field, allowedValues: iterations, type: 'select' };
			}
			return fields[field.referenceName]?.isIdentity ? { ...field, allowedValues: users } : field;
		});

		return getFormattedFields(fields, selectedIssueTypeFields);
	} catch (error) {
		logger.error('error for request: getProjectFields', fieldsUrl.href, 'Error', error);
		return keyBy([]);
	}
};

const getUser = async (): Promise<IAzureUser> => {
	try {
		const response = await innerCallApi(USER);
		const user = {
			id: response.id,
			email: response.emailAddress,
			displayName: response.displayName
		};
		return user;
	} catch (error) {
		logger.error('error for request: getUser', USER, 'Error', error);
		return {
			id: '',
			email: '',
			displayName: ''
		};
	}
};

const getOrganizations = async (): Promise<OptionType[]> => {
	try {
		const { id } = await getUser();
		const accountsUrl = new URL('https://app.vssps.visualstudio.com/_apis/accounts');
		accountsUrl.searchParams.set('memberId', id);
		accountsUrl.searchParams.set('api-version', '6.0');
		const organizations: IAzureOrganization[] = (await innerCallApi(accountsUrl.href)).value;
		return organizations.map((organization) => {
			const paths = getPaths(organization.accountUri);
			const organizationName = paths[paths.length - 2];
			return { label: organizationName, value: organizationName };
		});
	} catch (error) {
		logger.error('error for request: getOrganizations', 'Error', error);
		return null;
	}
};

const getProjectTypes = async (host: string): Promise<OptionType[]> => {
	const url: string = getProjectNameUrl(host);
	try {
		const requestUrl = appendAzurePath(url, PROJECT_TYPES);
		mergeApiVersionParams(requestUrl);
		const projectTypes: IAzureProjectType[] = (await innerCallApi(requestUrl.href)).value;
		return getProjectTypesOptions(projectTypes) ?? null;
	} catch (error) {
		logger.error('error for request: getProjectTypes', url, 'Error', error);
		return [];
	}
};

const openAzureTicket = (url: string): void => {
	openUrlInNewTab(url);
};

const getAzureWorkItemCreateRelativePath = (
	apiVersionQuery: string,
	workItemTypeName: string,
	bypassProcessRules = true
): string => {
	const params = new URLSearchParams();
	if (bypassProcessRules) {
		params.set(BYPASS_RULES_QUERY_PARAM, BYPASS_RULES_QUERY_VALUE);
	}
	const versionParams = new URLSearchParams(apiVersionQuery);
	versionParams.forEach((value, key) => {
		params.set(key, value);
	});
	const serializedQuery = params.toString();
	const resource = `${WORK_ITEMS}$${workItemTypeName}`;
	return serializedQuery === '' ? resource : `${resource}?${serializedQuery}`;
};

const createIssue = async (
	host: string,
	selectedIssueType: string,
	screenshotUrlBase64: string,
	fields: IAzureFields,
	bypassProcessRules = true
): Promise<boolean> => {
	const attachmentHost: string = getProjectNameUrl(host);

	try {
		let payload = getFieldsMetaData(fields);

		if (screenshotUrlBase64) {
			const attachmentFile = dataURItoBlob(screenshotUrlBase64);
			const attachmentUrl = appendAzurePath(attachmentHost, ATTACHMENT);
			mergeApiVersionParams(attachmentUrl);
			const attachment = await innerCallApi(
				attachmentUrl.href,
				'POST',
				attachmentFile,
				true,
				'application/octet-stream'
			);

			payload = addAttachmentToPayload(attachment.url, payload);
		}
		const workItemUrl = appendAzurePath(
			attachmentHost,
			getAzureWorkItemCreateRelativePath(API_VERSION, selectedIssueType, bypassProcessRules)
		);
		const response = await innerCallApi(workItemUrl.href, 'POST', payload);

		if (response._links.html.href) {
			openAzureTicket(response._links.html.href);
		}
		return !!response;
	} catch (error) {
		logger.error('error for request: createIssue', attachmentHost, 'Error', error);
		return false;
	}
};

export {
	appendAzurePath,
	createIssue,
	getAndSaveAzureToken,
	getAzureAuthURL,
	getAzureWorkItemCreateRelativePath,
	getOrganizations,
	getProjectFields,
	getProjects,
	getProjectTypes,
	mergeApiVersionParams
};
